import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format, isValid, parse, parseISO } from "date-fns";

import { useAppSelector } from "../../shared/hook";
import Loader from "../../shared/Ui/Loader/Loader";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import ModalComponent from "../../shared/Ui/ModalComponent/ModalWindow";
import ModalButton from "../../shared/Ui/Buttons/ModalButtons";
import {
  exportReport,
  exportReportPdf,
  getReport,
  ReportDetailsResponse,
} from "../../common/api/billingApi";
import {
  downloadPdfBlob,
  downloadXlsxBlob,
  formatBalanceReportAmount,
  formatProductGroups,
  useProductGroupNames,
} from "./billingUtils";

const toDisplayDate = (value?: string) => {
  if (!value) return "-";
  const iso = parseISO(value);
  if (isValid(iso)) {
    return value.includes("T")
      ? format(iso, "dd.MM.yyyy HH:mm")
      : format(iso, "dd.MM.yyyy");
  }
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  if (isValid(parsed)) return format(parsed, "dd.MM.yyyy");
  return value;
};

function BalanceReportDetailsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reportId } = useParams();
  const productGroupNames = useProductGroupNames();
  const userData: any = useAppSelector((state) => state.auth.userData);
  const isOperator = userData?.selectRoleV2?.some(
    (role: { roleId: number }) => role.roleId === 5,
  );

  const [report, setReport] = useState<ReportDetailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (!reportId) return;
    setIsLoading(true);
    getReport(Number(reportId))
      .then((data) => {
        setReport(data);
      })
      .catch((error) => {
        console.error("Error fetching report:", error);
        setReport(null);
      })
      .finally(() => setIsLoading(false));
  }, [reportId]);

  const isGenerating = report?.status === "GENERATING";
  useEffect(() => {
    if (!isGenerating || !reportId) return;
    const timer = window.setInterval(() => {
      getReport(Number(reportId))
        .then(setReport)
        .catch((error) => console.error("Error polling report:", error));
    }, 3000);
    return () => window.clearInterval(timer);
  }, [isGenerating, reportId]);

  const handleExportExcel = async () => {
    if (!report?.id) return;
    setIsExporting(true);
    try {
      const blob = await exportReport(report.id);
      downloadXlsxBlob(
        blob,
        `Балансовый отчет ${report.dateFrom} - ${report.dateTo}.xlsx`,
      );
    } catch (error) {
      console.error("Error exporting balance report:", error);
      window.alert(t("billing.exportError"));
    } finally {
      setIsExporting(false);
      setIsDownloadModalOpen(false);
    }
  };

  const handleExportPdf = async () => {
    if (!report?.id) return;
    setIsExporting(true);
    try {
      const blob = await exportReportPdf(report.id);
      downloadPdfBlob(
        blob,
        `Балансовый отчет ${report.dateFrom} - ${report.dateTo}.pdf`,
      );
    } catch (error) {
      console.error("Error exporting balance report to PDF:", error);
      window.alert(t("billing.exportError"));
    } finally {
      setIsExporting(false);
      setIsDownloadModalOpen(false);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (!report) {
    return (
      <div className="p-4">
        <p className="text-gray-600">{t("billing.noData")}</p>
        <button
          type="button"
          className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded mt-4"
          onClick={() => navigate("/billing/balance-report")}
        >
          {t("billing.backToJournal")}
        </button>
      </div>
    );
  }

  const rows = report.rows ?? [];
  const isError = report.status === "ERROR";

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <h2 className="my-3 text-xl font-semibold">
        {t("billing.balanceReportTitle")}
      </h2>

      {isGenerating ? (
        <div className="bg-white rounded-xl shadow-md p-8 mb-5 flex flex-col items-center justify-center gap-3">
          <div className="w-10 h-10 border-4 border-green/30 border-t-green rounded-full animate-spin" />
          <p className="text-sm text-gray-600">
            {t("billing.reportStatusGenerating")}...
          </p>
          <button
            type="button"
            className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
            onClick={() => navigate("/billing/balance-report")}
          >
            {t("billing.close")}
          </button>
        </div>
      ) : isError ? (
        <div className="bg-white rounded-xl shadow-md p-5 mb-5">
          <EmptyListPlaceholderContent
            title={report.errorMessage || t("billing.reportStatusError")}
          />
          <div className="flex justify-start mt-4">
            <button
              type="button"
              className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
              onClick={() => navigate("/billing/balance-report")}
            >
              {t("billing.close")}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-5">
          <div className="flex flex-row items-start justify-between gap-4 mb-4 flex-wrap">
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="font-semibold">
                  {t("billing.reportNumber")}:
                </span>{" "}
                {report.number ?? (report.id ? `№ ${report.id}` : "—")}
              </p>
              <p>
                <span className="font-semibold">
                  {t("billing.generatedAt")}:
                </span>{" "}
                {toDisplayDate(report.completedAt ?? report.createdAt)}
              </p>
              <p>
                <span className="font-semibold">
                  {t("billing.reportPeriod")}:
                </span>{" "}
                {toDisplayDate(report.dateFrom)} — {toDisplayDate(report.dateTo)}
              </p>
              {report.executorFio && (
                <p>
                  <span className="font-semibold">
                    {t("billing.executor")}:
                  </span>{" "}
                  {report.executorFio}
                </p>
              )}
              {report.orgIinBins && report.orgIinBins.length > 0 && (
                <p>
                  <span className="font-semibold">
                    {t("billing.orgBinShort")}:
                  </span>{" "}
                  {report.orgIinBins.join(", ")}
                </p>
              )}
              {report.productGroupCodes && report.productGroupCodes.length > 0 && (
                <p>
                  <span className="font-semibold">
                    {t("billing.productGroup")}:
                  </span>{" "}
                  {formatProductGroups(report.productGroupCodes, productGroupNames)}
                </p>
              )}
            </div>
            <div className="flex flex-row gap-2 items-start">
              <button
                type="button"
                className="bg-green text-white font-semibold py-2 px-4 rounded disabled:opacity-60"
                disabled={isExporting || !report.id || rows.length === 0}
                onClick={() => setIsDownloadModalOpen(true)}
              >
                {t("mainButtons.download")}
              </button>
              <button
                type="button"
                className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
                onClick={() => navigate("/billing/balance-report")}
              >
                {t("billing.close")}
              </button>
            </div>
          </div>

          <div className="rounded-lg overflow-x-auto">
            <table className="max-w-full w-full leading-normal">
              <thead className="border-b-2 border-gray-300">
                <tr>
                  <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                    №
                  </th>
                  {isOperator && (
                    <>
                      <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                        {t("billing.orgBinShort")}
                      </th>
                      <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                        {t("billing.orgNameShort")}
                      </th>
                    </>
                  )}
                  <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                    {t("billing.date")}
                  </th>
                  <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                    {t("billing.balanceInShort")}
                  </th>
                  <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                    {t("billing.writeOffShort")}
                  </th>
                  <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                    {t("billing.receiptsAmount")}
                  </th>
                  <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                    {t("billing.balanceOutShort")}
                  </th>
                </tr>
              </thead>
              {rows.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={isOperator ? 8 : 6} className="text-center py-5">
                      <EmptyListPlaceholderContent
                        title={t("billing.noDataForReport")}
                      />
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody className="font-normal text-center">
                  {rows.map((row, index) => (
                    <tr key={`balance-row-${row.orgIinBin ?? ""}-${row.date ?? index}`}>
                      <td className="px-5 py-4 text-sm border-b border-gray-200 text-gray-600">
                        {index + 1}
                      </td>
                      {isOperator && (
                        <>
                          <td className="px-5 py-4 text-sm border-b border-gray-200 text-gray-600">
                            {row.orgIinBin || "-"}
                          </td>
                          <td className="px-5 py-4 text-sm border-b border-gray-200 text-gray-600">
                            {row.orgName?.trim() || "-"}
                          </td>
                        </>
                      )}
                      <td className="px-5 py-4 text-sm border-b border-gray-200 text-gray-600">
                        {toDisplayDate(row.date)}
                      </td>
                      <td className="px-5 py-4 text-sm border-b border-gray-200 text-gray-700">
                        {formatBalanceReportAmount(row.balanceIn)}
                      </td>
                      <td className="px-5 py-4 text-sm border-b border-gray-200 text-red-600">
                        {formatBalanceReportAmount(row.writeOffAmount, {
                          signed: "writeOff",
                        })}
                      </td>
                      <td className="px-5 py-4 text-sm border-b border-gray-200 text-blue-600">
                        {formatBalanceReportAmount(row.topUpAmount, {
                          signed: "receipt",
                        })}
                      </td>
                      <td className="px-5 py-4 text-sm border-b border-gray-200 text-gray-700 font-medium">
                        {formatBalanceReportAmount(row.balanceOut)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td
                      className="px-5 py-4 text-sm border-b border-gray-200 text-gray-700 text-left"
                      colSpan={isOperator ? 4 : 2}
                    >
                      {t("billing.total")}
                    </td>
                    <td className="px-5 py-4 text-sm border-b border-gray-200 text-gray-500">
                      —
                    </td>
                    <td className="px-5 py-4 text-sm border-b border-gray-200 text-red-600">
                      {formatBalanceReportAmount(report.totalWriteOffAmount, {
                        signed: "writeOff",
                      })}
                    </td>
                    <td className="px-5 py-4 text-sm border-b border-gray-200 text-blue-600">
                      {formatBalanceReportAmount(report.totalTopUpAmount, {
                        signed: "receipt",
                      })}
                    </td>
                    <td className="px-5 py-4 text-sm border-b border-gray-200 text-gray-500">
                      —
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
          {rows.length > 0 && (
            <p className="text-xs text-gray-500 mt-3">
              * {t("billing.totalBalancesNote")}
            </p>
          )}
        </div>
      )}
      <ModalComponent
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        headerContent={t("orderList2.downloadFile")}
        content={
          <div className="flex flex-col space-y-2">
            <button
              type="button"
              className="bg-green text-white font-semibold py-2 px-4 rounded hover:opacity-90 transition duration-300"
              onClick={handleExportExcel}
            >
              {t("mainButtons.download")} Excel
            </button>
            <button
              type="button"
              className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:opacity-90 transition duration-300"
              onClick={handleExportPdf}
            >
              {t("mainButtons.download")} PDF
            </button>
          </div>
        }
        customContent={
          <ModalButton
            type="button"
            color="grey"
            onClick={() => setIsDownloadModalOpen(false)}
          >
            {t("mainButtons.close")}
          </ModalButton>
        }
      />
    </div>
  );
}

export default BalanceReportDetailsPage;
